import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Calculator, Flame, Droplets, Target, Dumbbell, Loader2, ChevronDown, Apple, Beef } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function FitnessCalculator({ childId, childData }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [sportType, setSportType] = useState('team_sports');
  const [activityLevels, setActivityLevels] = useState([]);
  const [sportTypes, setSportTypes] = useState([]);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const res = await axios.get(`${API}/fitness-calc/activity-levels`);
      setActivityLevels(res.data.levels);
      setSportTypes(res.data.sport_types);
    } catch (err) {
      console.error('Failed to fetch options');
    }
  };

  const calculate = async () => {
    if (!childData) return;
    
    setLoading(true);
    try {
      // Get latest measurement for height/weight
      const measureRes = await axios.get(`${API}/measurements/${childId}`);
      const measurements = measureRes.data;
      const latest = measurements[0];
      
      if (!latest) {
        toast.error('No measurements found. Please add height and weight first.');
        setLoading(false);
        return;
      }

      const age = childData.date_of_birth 
        ? (new Date() - new Date(childData.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000)
        : 12;

      const res = await axios.post(`${API}/fitness-calc/calculate`, {
        child_id: childId,
        height_cm: latest.standing_height,
        weight_kg: latest.weight,
        age: age,
        gender: childData.gender?.toLowerCase() || 'male',
        activity_level: activityLevel,
        sport_type: sportType
      });
      
      setResult(res.data);
      toast.success('Fitness metrics calculated!');
    } catch (err) {
      toast.error('Failed to calculate fitness metrics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid="fitness-calculator">
      <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
        <Calculator className="h-5 w-5 text-[#02b5ff]" />
        Fitness Calculator
      </h3>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Activity Level</label>
          <select
            value={activityLevel}
            onChange={(e) => setActivityLevel(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            {activityLevels.map((level) => (
              <option key={level.id} value={level.id}>{level.name} - {level.description}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Sport Type</label>
          <select
            value={sportType}
            onChange={(e) => setSportType(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            {sportTypes.map((sport) => (
              <option key={sport.id} value={sport.id}>{sport.name} ({sport.adjustment})</option>
            ))}
          </select>
        </div>
      </div>

      <Button
        onClick={calculate}
        disabled={loading}
        className="w-full bg-gradient-to-r from-[#02b5ff] to-[#0099dd] text-white rounded-lg mb-4"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />}
        Calculate Metrics
      </Button>

      {result && (
        <div className="space-y-4">
          {/* Main Metrics */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <Flame className="h-5 w-5 text-orange-500 mx-auto mb-1" />
              <div className="text-xl font-bold text-white">{result.metrics.bmr_kcal}</div>
              <div className="text-xs text-zinc-500">BMR (kcal)</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <Target className="h-5 w-5 text-[#02b5ff] mx-auto mb-1" />
              <div className="text-xl font-bold text-white">{result.metrics.tdee.adjusted_tdee}</div>
              <div className="text-xs text-zinc-500">Daily Calories</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <Droplets className="h-5 w-5 text-blue-400 mx-auto mb-1" />
              <div className="text-xl font-bold text-white">{(result.metrics.hydration_ml / 1000).toFixed(1)}L</div>
              <div className="text-xs text-zinc-500">Water/Day</div>
            </div>
          </div>

          {/* Macros */}
          <div className="bg-zinc-800/30 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <Apple className="h-4 w-4 text-green-500" />
              Daily Macros Target
            </h4>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-lg font-bold text-[#ff025f]">{result.metrics.macros.protein_g}g</div>
                <div className="text-xs text-zinc-500">Protein ({result.metrics.macros.protein_pct}%)</div>
              </div>
              <div>
                <div className="text-lg font-bold text-yellow-500">{result.metrics.macros.carbs_g}g</div>
                <div className="text-xs text-zinc-500">Carbs ({result.metrics.macros.carbs_pct}%)</div>
              </div>
              <div>
                <div className="text-lg font-bold text-[#02b5ff]">{result.metrics.macros.fat_g}g</div>
                <div className="text-xs text-zinc-500">Fat ({result.metrics.macros.fat_pct}%)</div>
              </div>
            </div>
          </div>

          {/* Body Composition */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-zinc-800/30 rounded-lg p-3">
              <div className="text-xs text-zinc-400 mb-1">Body Fat Estimate</div>
              <div className="text-lg font-bold text-white">{result.metrics.body_fat_estimate.percentage}%</div>
              <div className="text-xs text-[#02b5ff]">{result.metrics.body_fat_estimate.category}</div>
            </div>
            <div className="bg-zinc-800/30 rounded-lg p-3">
              <div className="text-xs text-zinc-400 mb-1">Ideal Weight Range</div>
              <div className="text-lg font-bold text-white">{result.metrics.ideal_weight.average}kg</div>
              <div className="text-xs text-zinc-500">{result.metrics.ideal_weight.miller}-{result.metrics.ideal_weight.hamwi}kg</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
